import cn from 'classnames';
import { graphql, useFragment } from 'react-relay';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';
import { type FC, useRef, useState } from 'react';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { LABEL_VALUE_MONITORING_GROUP_PDP } from 'dibs-tracking/src/prometheus/peformanceTracking';
import { usePdpRespMarketplaceLayoutPrimaryContext } from '../PdpRespMarketplaceLayout/PdpRespMarketplaceLayoutPrimaryContextProvider';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import {
    usePdpPricingAndSaleVariant,
    useTrackPdpPricingAndSaleTest,
} from '../../hooks/usePdpPricingAndSaleVariant';
import { usePdpTopYmalSponsoredTestTracking } from '../../hooks/usePdpTopYmalSponsoredTestTracking';
import { useTrackPdpEstimateSimilarSoldTest } from '../../hooks/usePdpEstimateSimilarSoldTest';
import { useShowPdpEstimateInTab } from '../../hooks/usePdpEstimateSimilarSold';

// components
import { PdpSharedPriceDisplay } from '../../PdpSharedPriceDisplay/PdpSharedPriceDisplay';
import { PriceDisplayVariant } from 'dibs-buyer-price/exports/PriceDisplayVariant';
import { IncludedLabels } from 'dibs-buyer-price/exports/IncludedLabels';
import { useShowIncludedLabelsInline } from 'dibs-buyer-price/exports/useShowIncludedLabelsInline';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { PdpSharedPromoCode } from '../../PdpSharedPromoCode/PdpSharedPromoCode';
import { PdpSharedUrgencyLazy } from '../../PdpSharedUrgency/PdpSharedUrgencyLazy';
import { SharedPerformanceTracker } from '../../../shared/SharedPerformanceTracker';
import { PdpRespSidebarBlurbs } from '../../PdpRespSidebarBlurbs/PdpRespSidebarBlurbs';
import { PdpSharedQuantitySelect } from '../../PdpSharedButtons/PdpSharedQuantitySelect';
import { PdpRespSwatchSelector } from '../../PdpRespSwatchSelector/PdpRespSwatchSelector';
import { PdpRespSidebarButtons } from '../../PdpRespSidebarButtons/PdpRespSidebarButtons';
import { PdpRespSidebarDetails } from '../../PdpRespSidebarDetails/PdpRespSidebarDetails';
import { PdpRespDealerChatLoader } from '../../PdpRespDealerChat/PdpRespDealerChatLoader';
import { PdpSharedDesignYourOwnPillLazy } from '../../PdpSharedDesignYourOwnPill/PdpSharedDesignYourOwnPillLazy';
import { PdpRespSharedTitle } from '../../respShared/PdpRespSharedTitle/PdpRespSharedTitle';
import { PricingGuidanceInfo } from 'dibs-pricing-guidance/exports/PricingGuidanceInfo';
import { PdpSharedPageTracker } from '../../shared/PdpSharedPageTracker/PdpSharedPageTracker';
import { PdpRespIconBubblesWrapper } from '../../PdpRespIconBubbles/PdpRespIconBubblesWrapper';
import EstimatedValue from 'dibs-pricing-guidance/exports/EstimatedValue';
import PdpSharedEstimateSimilarSoldRange from '../../PdpSharedEstimateSimilarSold/PdpSharedEstimateSimilarSoldRange';
import { PdpRespDetailTabs, useDetailTabsState } from '../../PdpRespDetailTabs/PdpRespDetailTabs';
import { PdpRespSimilarItemsLanding } from '../../PdpRespSimilarItems/PdpRespSimilarItemsLanding';
import { PdpRespSharedQrCodeLazy } from '../../respShared/PdpRespSharedQrCode/PdpRespSharedQrCodeLazy';
import { PdpSharedPrivateOfferBanner } from '../../PdpSharedPrivateOfferBanner/PdpSharedPrivateOfferBanner';
import { PdpRespSharedBreadcrumbs } from '../../respShared/PdpRespSharedBreadcrumbs/PdpRespSharedBreadcrumbs';
import { PdpRespSharedHeroCarousel } from '../../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel';
import { PdpRespMarketplaceStickyHeader } from '../PdpRespMarketplaceStickyHeader/PdpRespMarketplaceStickyHeader';
import { PdpRespSidebarSellerInfoAndContactLazy } from '../../PdpRespSidebarSellerInfo/PdpRespSidebarSellerInfoAndContactLazy';
import { PdpRespMarketplaceStickyHeaderSwitch } from '../PdpRespMarketplaceStickyHeader/PdpRespMarketplaceStickyHeaderSwitch';

// types
import { type PdpRespMarketplaceContainerPrimary_viewer$key as Viewer } from './__generated__/PdpRespMarketplaceContainerPrimary_viewer.graphql';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespMarketplaceContainerPrimary.scss';

// lazy components
const PdpRespPrintHeader = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespPrintHeader" */ '../../PdpRespPrintHeader/PdpRespPrintHeader'
        )
);

const PdpSharedItemFavorites = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedItemFavorites" */ '../../shared/PdpSharedItemFavorites/PdpSharedItemFavorites'
        )
);

const PdpSharedRestrictedMaterials = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedRestrictedMaterials" */ '../../shared/PdpSharedRestrictedMaterials/PdpSharedRestrictedMaterials'
        )
);

const PdpRespMarketplaceNotifications = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespMarketplaceNotifications" */ '../PdpRespMarketplaceNotifications/PdpRespMarketplaceNotifications'
        )
);

const PdpRespSharedPrintDetails = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespSharedPrintDetails" */ '../../respShared/PdpRespSharedPrintDetails/PdpRespSharedPrintDetails'
        )
);

const MerchandizingTool = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "MerchandizingTool" */ 'dibs-merchandizing-tools/exports/MerchandizingTool'
        )
);

const viewerFragment = graphql`
    fragment PdpRespMarketplaceContainerPrimary_viewer on Viewer {
        ...PdpRespDetailTabs_viewer @skip(if: $isPDPDesktopAggregationVariant)
        ...PdpSharedPromoCode_viewer
        ...PdpRespSharedHeroCarousel_viewer
        ...PdpRespIconBubblesWrapper_viewer
        ...PdpSharedPageTracker_viewer @include(if: $afterDisplay)
        ...PdpRespSidebarBlurbs_viewer @include(if: $afterDisplay)
        ...PdpRespDealerChatLoader_viewer @include(if: $afterDisplay)
        ...PdpSharedRestrictedMaterials_refactor_viewer @include(if: $afterDisplay)
        ...PdpRespSidebarDetails_viewer @include(if: $isPDPDesktopAggregationVariant)
        ... @include(if: $isEstimateSimilarSoldVariant) {
            ...usePdpEstimateSimilarSold_viewer @include(if: $afterDisplay)
        }
        itemRead(itemIds: $itemIds) {
            ... on Item {
                itemIsOnHold: isOnHold(buyerId: $userId)
                seller {
                    isAccessible
                    ...PdpRespSidebarSellerInfoAndContactLazy_seller @include(if: $afterDisplay)
                }
                displayInfo(page: pdp, buyerId: $userId) {
                    ctas {
                        showAcceptOffer
                    }
                    ...PdpSharedPromoCode_displayInfo
                    ...PdpRespSidebarBlurbs_displayInfo @include(if: $afterDisplay)
                    ...PdpRespSidebarButtons_displayInfo @include(if: $afterDisplayOrBuaSsr)
                    ...PdpRespMarketplaceStickyHeader_displayInfo
                        @include(if: $isPDPDesktopAggregationVariant)
                }
                skus {
                    serviceId
                    ...PdpRespSidebarBlurbs_sku @include(if: $afterDisplay)
                    ...PdpRespMarketplaceStickyHeader_sku
                        @include(if: $isPDPDesktopAggregationVariant)
                    ... on ItemSkuType @include(if: $isMultiSku) {
                        displayPrice(
                            isTrade: $isTrade
                            buyerId: $userId
                            userCountryCode: $userCountryCode
                            priceBookName: $priceBookName
                        ) {
                            ...PriceDisplayVariant_displayPrice
                            ...IncludedLabels_displayPrice
                            ...useShowIncludedLabelsInline_displayPrice
                        }
                    }
                }
                ...PdpSharedPriceDisplay_item @include(if: $afterDisplayOrBuaSsr)
                ... on Item @include(if: $afterDisplayOrBuaSsr) {
                    ...PriceDisplayVariant_item
                    ...IncludedLabels_item
                    ...useShowIncludedLabelsInline_item
                    displayPrice(
                        isTrade: $isTrade
                        buyerId: $userId
                        userCountryCode: $userCountryCode
                        priceBookName: $priceBookName
                    ) {
                        ...PriceDisplayVariant_displayPrice
                        ...IncludedLabels_displayPrice
                        ...useShowIncludedLabelsInline_displayPrice
                    }
                }
                ...PdpRespDetailTabs_item @skip(if: $isPDPDesktopAggregationVariant)
                ...PdpRespSharedTitle_item
                ...PdpSharedDesignYourOwnPillLazy_item
                ...EstimatedValue_item @include(if: $afterDisplay)
                ...PdpRespSharedBreadcrumbs_item
                ...PdpRespIconBubblesWrapper_item
                ...PdpRespSharedHeroCarousel_item
                ...PdpRespSimilarItemsLanding_item
                ...PricingGuidanceInfo_item @include(if: $afterDisplay)
                ...PdpRespSwatchSelector_item @include(if: $isMultiSku)
                ...PdpRespSidebarBlurbs_item @include(if: $afterDisplay)
                ...PdpSharedItemFavorites_item @include(if: $afterDisplay)
                ...PdpRespSharedPrintDetails_item @include(if: $afterDisplay)
                ...PdpRespSidebarButtons_item @include(if: $afterDisplayOrBuaSsr)
                ...PdpRespMarketplaceNotifications_item @include(if: $afterDisplay)
                ...PdpSharedQuantitySelect_item @include(if: $afterDisplayOrBuaSsr)
                ...PdpRespSidebarSellerInfoAndContactLazy_item @include(if: $afterDisplay)
                ...PdpRespSidebarDetails_item @include(if: $isPDPDesktopAggregationVariant)
                ...PdpRespMarketplaceStickyHeader_item @include(if: $isPDPDesktopAggregationVariant)
                ... @include(if: $isEstimateSimilarSoldVariant) {
                    ...PdpSharedEstimateSimilarSoldRange_item @include(if: $afterDisplay)
                    ...usePdpEstimateSimilarSold_item @include(if: $afterDisplay)
                }
                ...usePdpPricingAndSaleVariant_item
                ...usePdpEstimateSimilarSoldTest_item @include(if: $afterDisplay)
            }
        }
        user(userId: $userId) @include(if: $fetchUser) {
            hasMerchandizingPermission: hasPermission(name: "BUYER_FE_MERCH_TOOL")
            ...PdpRespDetailTabs_user @skip(if: $isPDPDesktopAggregationVariant)
            ...PdpSharedPromoCode_user
            ...PdpRespIconBubblesWrapper_user
            ...PdpRespSharedHeroCarousel_user
            ...PdpRespSimilarItemsLanding_user
            ...PdpRespSwatchSelector_user @include(if: $isMultiSku)
            ...PdpRespSidebarBlurbs_user @include(if: $afterDisplay)
            ...PdpSharedItemFavorites_user @include(if: $afterDisplay)
            ...PdpRespSharedPrintDetails_user @include(if: $afterDisplay)
            ...PdpRespSidebarButtons_user @include(if: $afterDisplayOrBuaSsr)
            ...PdpRespMarketplaceNotifications_user @include(if: $afterDisplay)
            ...PdpRespSidebarSellerInfoAndContactLazy_user @include(if: $afterDisplay)
            ...PdpRespSidebarDetails_user @include(if: $isPDPDesktopAggregationVariant)
            ...PdpRespMarketplaceStickyHeader_user @include(if: $isPDPDesktopAggregationVariant)
        }
        sponsoredItems(
            userId: $userId
            guestId: $guestId
            itemId: $itemId
            isTrade: $isTrade
            priceBookName: $priceBookName
            userCountryCode: $userCountryCode
            pageSize: 7
        ) @include(if: $afterDisplay) @skip(if: $skipSponsoredItems) {
            ...PdpRespSimilarItemsLanding_sponsoredItems
        }
        similarLandingItems: moreLikeThisItem(
            first: $similarItemsCount
            itemId: $itemId
            isTrade: $isTrade
            onlyAuctionItems: true
            useML: true
            mlType: $mlType
        ) {
            ...PdpRespSimilarItemsLanding_similarItems
        }
        ... @include(if: $afterDisplay) {
            buyerUrgencyInfo(itemId: $itemId, userId: $userId) {
                ...PdpSharedUrgencyLazy_buyerUrgencyInfo
                itemUrgency {
                    hasUrgencyInfo
                }
            }
        }
    }
`;

export const PdpRespMarketplaceContainerPrimary: FC<{ viewer: Viewer }> = ({
    viewer: viewerRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const detailsSectionRef = useRef<HTMLDivElement>(null);
    const similarItemsScrollRef = useRef<HTMLDivElement>(null);
    const scrollToCustomizationRef = useRef<HTMLDivElement>(null);
    const [itemQuantity, setItemQuantity] = useState<number | null>(null);
    const { onSkuChange, selectedSkuId, currency, printWithPrice, relayVariables } =
        usePdpRespMarketplaceLayoutPrimaryContext();
    const { isTablet, isPdpRespPreviewPage, isPdpEstimateSimilarSoldTestVariant } =
        useServerVarsContext();
    const {
        userId,
        itemId,
        isTrade,
        isMultiSku,
        afterDisplay,
        afterDisplayOrBuaSsr,
        isPDPDesktopAggregationVariant = undefined,
    } = relayVariables?.variables || {};
    const { itemRead, user = null, similarLandingItems, buyerUrgencyInfo, sponsoredItems } = viewer;
    const item = itemRead?.[0];
    const { hasUrgencyInfo } = buyerUrgencyInfo?.itemUrgency || {};

    const detailTabsState = useDetailTabsState();

    const selectedSku = item?.skus?.find(sku => sku?.serviceId === selectedSkuId) || null;

    const isPdpPricingAndSaleVariant = usePdpPricingAndSaleVariant({ item });
    useTrackPdpPricingAndSaleTest({ item });
    const displayPrice = (selectedSku?.displayPrice || item?.displayPrice)?.filter(filterNulls);
    const showIncludedLabelsInline = useShowIncludedLabelsInline({
        item: afterDisplayOrBuaSsr ? item : null,
        displayPrice: afterDisplayOrBuaSsr ? displayPrice : null,
        currency,
    });
    usePdpTopYmalSponsoredTestTracking();

    const showPdpEstimateInTab = useShowPdpEstimateInTab({
        viewer: afterDisplay && isPdpEstimateSimilarSoldTestVariant ? viewer : null,
        item: afterDisplay && isPdpEstimateSimilarSoldTestVariant ? item : null,
        currency,
    });
    useTrackPdpEstimateSimilarSoldTest({ item: afterDisplay ? item : null, currency });

    if (!item) {
        return null;
    }

    const { itemIsOnHold, seller, displayInfo } = item;
    const { showAcceptOffer } = displayInfo?.ctas || {};
    const isSellerAccessible = !!seller?.isAccessible;
    const includePromoCode = !itemIsOnHold && isSellerAccessible;

    return (
        <>
            {afterDisplay && <PdpSharedPageTracker viewer={viewer} selectedSkuId={selectedSkuId} />}
            {afterDisplay && (
                <ClientSuspense fallback={null}>
                    <div className={styles.printShow}>
                        <PdpRespPrintHeader itemId={itemId} />
                    </div>
                </ClientSuspense>
            )}
            {afterDisplay && isPDPDesktopAggregationVariant && (
                <PdpRespMarketplaceStickyHeader
                    item={item}
                    user={user}
                    currency={currency}
                    displayInfo={displayInfo}
                    skuId={selectedSkuId || undefined}
                    sku={isMultiSku ? selectedSku : null}
                    itemQuantity={itemQuantity || undefined}
                />
            )}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsLanding
                    sponsoredItems={sponsoredItems}
                    item={item}
                    user={user}
                    currency={currency}
                    similarItems={similarLandingItems}
                    scrollToRef={similarItemsScrollRef}
                />
                <PdpRespSharedBreadcrumbs item={item} />
            </div>
            <div className={cn('rowFlex', dibsCss.relative)} data-tn="pdp-main-container">
                {/* Image Carousel */}
                <div className={cn('colXs12 colLg7 colXl8', dibsCss.ptSmall, styles.topContainer)}>
                    <PdpRespSharedHeroCarousel
                        item={item}
                        user={user}
                        viewer={viewer}
                        currency={currency}
                        afterDisplay={!!afterDisplay}
                        selectedSkuId={selectedSkuId}
                        size={isTablet ? 'medium' : 'large'}
                        disableModals={isPdpRespPreviewPage}
                        isPDPDesktopAggregationVariant={isPDPDesktopAggregationVariant}
                    >
                        <PdpRespIconBubblesWrapper
                            item={item}
                            user={user}
                            viewer={viewer}
                            skuId={selectedSkuId}
                            relayVariables={{ isTrade: !!isTrade, isPDPDesktopAggregationVariant }}
                        />
                    </PdpRespSharedHeroCarousel>
                </div>
                {/* Sidebar */}
                <div
                    className={cn(
                        'colXs12 colLg5 colXl4',
                        styles.topContainer,
                        styles.sidebarContainer,
                        {
                            [styles.isPDPDesktopAggregationVariant]: isPDPDesktopAggregationVariant,
                        }
                    )}
                >
                    <div className={'rowFlex'}>
                        <div className="colXs12 colLg10">
                            <div className={styles.urgencyPlaceholder}>
                                {afterDisplay && buyerUrgencyInfo && hasUrgencyInfo && (
                                    <PdpSharedUrgencyLazy buyerUrgencyInfo={buyerUrgencyInfo} />
                                )}
                                {afterDisplay && !hasUrgencyInfo && (
                                    <PdpSharedDesignYourOwnPillLazy
                                        item={item}
                                        scrollToCustomizationRef={scrollToCustomizationRef}
                                    />
                                )}
                            </div>
                            <PdpRespSharedTitle
                                item={item}
                                isPDPDesktopAggregationVariant={isPDPDesktopAggregationVariant}
                                isPdpPricingAndSaleVariant={isPdpPricingAndSaleVariant}
                            />
                        </div>
                        {/* Favorite / Folder */}
                        <div className={cn('colXs2', styles.printHide)}>
                            {afterDisplay && (
                                <ClientSuspense fallback={null}>
                                    <PdpSharedItemFavorites
                                        item={item}
                                        user={user}
                                        showHeartCount
                                        wrapperClass={cn(
                                            dibsCss.mt0,
                                            dibsCss.top0,
                                            dibsCss.absolute,
                                            dibsCss.contents,
                                            dibsCss.rightXsmall
                                        )}
                                        heartIconWrapperClass={dibsCss.mt0}
                                    />
                                </ClientSuspense>
                            )}
                        </div>

                        <div className={cn(dibsCss.wFull, dibsCss.pxXsmall)}>
                            {afterDisplay && showAcceptOffer && !isPdpPricingAndSaleVariant && (
                                <PdpSharedPrivateOfferBanner />
                            )}
                            <div className={cn({ [styles.printHide]: !printWithPrice })}>
                                {afterDisplay && (
                                    <SharedPerformanceTracker
                                        datadogTimer={{ name: 'pdp_time_to_price' }}
                                        measureFromPageInit={{
                                            subGroup: 'resp/price-box',
                                            monitoringGroup: LABEL_VALUE_MONITORING_GROUP_PDP,
                                        }}
                                    />
                                )}
                                {isPDPDesktopAggregationVariant && (
                                    <PdpRespMarketplaceStickyHeaderSwitch />
                                )}
                                {afterDisplayOrBuaSsr &&
                                    (isPdpPricingAndSaleVariant ? (
                                        <PriceDisplayVariant
                                            item={item}
                                            displayPrice={displayPrice}
                                            currency={currency}
                                        />
                                    ) : (
                                        <PdpSharedPriceDisplay
                                            item={item}
                                            currency={currency}
                                            selectedSkuId={selectedSkuId}
                                        />
                                    ))}
                                {afterDisplay && isPdpEstimateSimilarSoldTestVariant && (
                                    <PdpSharedEstimateSimilarSoldRange
                                        item={item}
                                        currency={currency}
                                        estimatePricingRef={
                                            showPdpEstimateInTab
                                                ? scrollToCustomizationRef
                                                : similarItemsScrollRef
                                        }
                                    />
                                )}
                                {afterDisplay && !isPdpEstimateSimilarSoldTestVariant && (
                                    <EstimatedValue
                                        item={item}
                                        currency={currency}
                                        infoContent={
                                            <PricingGuidanceInfo
                                                item={item}
                                                iconClassName={dibsCss.fillSatan}
                                                trigger="siderail"
                                            />
                                        }
                                        textClassName={cn(dibsCss.sassyFontBodyXSmallHeavy, {
                                            [dibsCss.mt2px]: isPdpPricingAndSaleVariant,
                                        })}
                                    />
                                )}

                                {isPdpPricingAndSaleVariant &&
                                    afterDisplayOrBuaSsr &&
                                    !showIncludedLabelsInline && (
                                        <span className={cn(dibsCss.block, dibsCss.mt2px)}>
                                            <IncludedLabels
                                                item={item}
                                                displayPrice={displayPrice}
                                                currency={currency}
                                            />
                                        </span>
                                    )}

                                {includePromoCode && (
                                    <PdpSharedPromoCode
                                        displayInfo={displayInfo}
                                        itemId={itemId}
                                        viewer={viewer}
                                        user={user}
                                        userId={userId}
                                        currency={currency}
                                        excludeShippingPromos
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {isMultiSku && (
                        <PdpRespSwatchSelector
                            item={item}
                            user={user}
                            currency={currency}
                            isClient={afterDisplay}
                            showSwatchDescriptionOnHover
                            selectedSkuId={selectedSkuId}
                            onClick={(skuId: string) => onSkuChange(skuId)}
                        />
                    )}
                    {/* Item Ctas */}
                    {afterDisplayOrBuaSsr && (
                        <div
                            className={cn(
                                dibsCss.mtSmall,
                                dibsCss.mbXxsmall,
                                dibsCss.mx0,
                                styles.printHide,
                                {
                                    [dibsCss.mbXsmall]: isPDPDesktopAggregationVariant,
                                }
                            )}
                        >
                            <PdpSharedQuantitySelect
                                item={item}
                                selectedSkuId={selectedSkuId}
                                setItemQuantity={setItemQuantity}
                            >
                                {quantitySelected => (
                                    <PdpRespSidebarButtons
                                        user={user}
                                        item={item}
                                        itemSearch={null}
                                        relayVariables={{
                                            itemId,
                                            userId,
                                            isMultiSku: isMultiSku || undefined,
                                        }}
                                        displayInfo={displayInfo}
                                        skuId={selectedSkuId || undefined}
                                        quantitySelected={quantitySelected || undefined}
                                        currency={currency}
                                    />
                                )}
                            </PdpSharedQuantitySelect>
                        </div>
                    )}

                    {/* Restricted Materials */}
                    {afterDisplay && (
                        <ClientSuspense fallback={null}>
                            <PdpSharedRestrictedMaterials viewer={viewer} />
                        </ClientSuspense>
                    )}
                    {/* Blurbs */}
                    {afterDisplay && isSellerAccessible && (
                        <>
                            <PdpRespSidebarBlurbs
                                item={item}
                                user={user}
                                viewer={viewer}
                                currency={currency}
                                displayInfo={displayInfo}
                                sku={selectedSku || null}
                                itemQuantity={itemQuantity || undefined}
                                isPDPDesktopAggregationVariant={
                                    isPDPDesktopAggregationVariant || false
                                }
                                contactSellerComponent={
                                    isPDPDesktopAggregationVariant ? (
                                        <PdpRespSidebarSellerInfoAndContactLazy
                                            selectedSkuId={selectedSkuId || ''}
                                            user={user}
                                            seller={seller}
                                            item={item}
                                            itemQuantity={itemQuantity || undefined}
                                            currency={currency}
                                            isClient={afterDisplay}
                                        />
                                    ) : null
                                }
                            />
                            {afterDisplay && !isPDPDesktopAggregationVariant && (
                                <div
                                    className={cn(
                                        styles.printHide,
                                        dibsCss.ptSmall,
                                        dibsCss.borderT,
                                        dibsCss.borderTSolid,
                                        dibsCss.borderTDolphin
                                    )}
                                >
                                    {/* Questions about this piece? / Contact Dealer */}
                                    <PdpRespSidebarSellerInfoAndContactLazy
                                        user={user}
                                        item={item}
                                        seller={seller}
                                        currency={currency}
                                        isClient={afterDisplay}
                                        selectedSkuId={selectedSkuId || ''}
                                        itemQuantity={itemQuantity || undefined}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    {isPDPDesktopAggregationVariant && (
                        <>
                            {!afterDisplay && <div className={styles.loadingSpacer} />}
                            <PdpRespSidebarDetails
                                viewer={viewer}
                                item={item}
                                user={user}
                                skuId={selectedSkuId}
                                itemQuantity={itemQuantity}
                            />
                        </>
                    )}
                    <PdpRespSharedQrCodeLazy itemId={itemId} className={styles.printShow} />
                </div>
                {user?.hasMerchandizingPermission && userId && (
                    <ClientSuspense fallback={null}>
                        <div data-dibs-merchandizing-tool-item={itemId} />
                        <MerchandizingTool itemIds={[itemId]} userId={userId} placement="pdp" />
                    </ClientSuspense>
                )}
            </div>
            <div ref={scrollToCustomizationRef} />
            <div
                ref={detailsSectionRef}
                className={cn(dibsCss.mtMedium, dibsCss.mbLarge, styles.details, styles.printHide)}
            >
                {!isPDPDesktopAggregationVariant && (
                    <PdpRespDetailTabs
                        item={item}
                        user={user}
                        viewer={viewer}
                        currency={currency}
                        itemQuantity={itemQuantity}
                        tabsState={detailTabsState}
                        selectedSkuId={selectedSkuId}
                    />
                )}
            </div>
            {afterDisplay && <PdpRespDealerChatLoader viewer={viewer} currency={currency} />}
            {afterDisplay && (
                <div className={styles.printShow}>
                    <ClientSuspense fallback={null}>
                        <PdpRespSharedPrintDetails
                            item={item}
                            user={user}
                            currency={currency}
                            selectedSkuId={selectedSkuId}
                        />
                    </ClientSuspense>
                </div>
            )}
            <div ref={similarItemsScrollRef} />

            {/* page notifications */}
            {afterDisplay && (
                <ErrorBoundary fallback={() => null}>
                    <ClientSuspense fallback={null}>
                        <PdpRespMarketplaceNotifications item={item} user={user} />
                    </ClientSuspense>
                </ErrorBoundary>
            )}
        </>
    );
};
